import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Stack from '@mui/system/Stack';
import { DropzoneInputProps, DropzoneRootProps, useDropzone } from 'react-dropzone';
import { UploaderFileTypes } from '@askporter/utils';
import { RevampedIcon, RevampedIconSize } from '../../../RevampedIcon';
import { Typography } from '../../../Typography';
const StyledDragAndDropUploaderBox = styled('div', {
  shouldForwardProp: (props) => props !== 'requiredFileError' && props !== 'disabled' && props !== 'isDragActive',
})<
  Pick<DragAndDropUploaderBoxProps, 'requiredFileError' | 'disabled'> & {
    isDragActive: boolean;
  }
>(({ theme, requiredFileError, disabled, isDragActive }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '16px 24px',
  borderRadius: '12px',
  border: requiredFileError
    ? `1px solid ${theme.custom?.colours?.error600}`
    : `1px solid ${theme.custom?.colours?.grey200}`,
  color: disabled ? theme.custom?.colours?.grey500 : theme.custom?.colours?.grey600,
  outline: isDragActive ? `2px solid ${theme.palette.primary.main}` : 'none',
  background: disabled ? theme.custom?.colours?.grey50 : '#FFF',
  '&:hover': !disabled && {
    outline: `2px solid ${theme.palette.primary.main}`,
    cursor: 'pointer',
  },
  '&:focus': !disabled && {
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));
const HighlightedText = styled('span', {
  shouldForwardProp: (props) => props !== 'disabled',
})<Pick<DragAndDropUploaderBoxProps, 'disabled'>>(({ theme, disabled }) => ({
  ...theme.typography.body1,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  color: disabled ? theme.custom?.colours?.grey400 : theme.palette.primary.main,
}));
const StyledHelperText = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  marginTop: '4px !important',
  fontSize: '12px',
  lineHeight: '18px',
}));
interface DragAndDropUploaderBoxProps {
  /**
   * Translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * id prefix used for testing
   */
  idPrefix: string;
  /**
   * Is file upload disabled
   */
  disabled?: boolean;
  /*
   * If the file is required
   */
  required?: boolean;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Whether there is an unsupported file error
   */
  requiredFileError: boolean;
  /**
   * Dropzone props
   */
  dragAndDropProps?: {
    getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
    getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
    isDragActive: boolean;
  };
  /**
   * The accepted file types
   */
  accept?: UploaderFileTypes[];
  /**
   * Whether the file upload is multiple
   */
  multiple?: boolean;
  /**
   * The onDrop function
   */
  onDrop: (files: File[]) => void;
}
export const DragAndDropUploaderBox = ({
  requiredFileError,
  disabled,
  idPrefix,
  required,
  helperText,
  t,
  accept = [],
  multiple = true,
  onDrop,
}: DragAndDropUploaderBoxProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      onDrop(files);
    },
    multiple,
    disabled,
  });
  const theme = useTheme();
  return (
    <StyledDragAndDropUploaderBox
      {...getRootProps()}
      requiredFileError={requiredFileError}
      isDragActive={isDragActive}
      disabled={disabled}
      data-testid={`${idPrefix}-uploader-drag-and-drop`}
    >
      <input
        required={required}
        disabled={disabled}
        {...getInputProps({
          accept: accept?.length > 0 ? accept.join(',') : undefined,
        })}
        data-testid={`${idPrefix}-uploader-input`}
      />
      <Stack direction="column" spacing={2} alignItems="center" justifyContent={'center'}>
        <RevampedIcon
          fullPath="/media/icons/design-system/file-upload/cloud-icon/24px.svg"
          customSvgStyles={{
            border: '1px solid #EAECF0',
            borderRadius: '8px',
            padding: '10px',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
          }}
          firstPathStroke={disabled ? theme.custom?.colours?.grey400 : theme.custom?.colours?.grey700}
          iconSize={RevampedIconSize.MD}
        />
        <Typography variant="body1" sx={{ fontSize: '14px', marginTop: '12px !important', lineHeight: '20px' }}>
          <HighlightedText disabled={disabled}>{t('common:file_uploader:label_highlighted')}</HighlightedText>
          {t('common:file_uploader:label_rest')}
        </Typography>
        {requiredFileError ? (
          <StyledHelperText variant="body2" sx={{ color: theme.palette.error.main }}>
            {t('common:file_uploader:error:required_file')}
          </StyledHelperText>
        ) : (
          <StyledHelperText variant="body2">{helperText || t('common:file_uploader:helper_text')}</StyledHelperText>
        )}
      </Stack>
    </StyledDragAndDropUploaderBox>
  );
};
